import React from 'react';
import { useSubjects } from '../../contexts/SubjectsContext';
import { Filter, Search, X } from 'lucide-react';

const chapterLabels: Record<string, string> = {
  'croissance': 'Croissance économique',
  'commerce': 'Commerce international',
  'chomage': 'Chômage',
  'crise': 'Crises financières',
  'europe': 'Europe',
  'structure': 'Structure sociale',
  'ecole': 'École',
  'mobilite': 'Mobilité sociale',
  'travail': 'Travail-Emploi',
  'engagement': 'Engagement politique',
  'justice': 'Justice sociale',
  'environnement': 'Environnement'
};

const locationLabels: Record<string, string> = {
  'metropole': 'France métropolitaine',
  'asie': 'Asie',
  'amerique-nord': 'Amérique du Nord',
  'amerique-sud': 'Amérique du Sud',
  'polynesie': 'Polynésie',
  'etrangers': 'Centres étrangers',
  'reunion': 'La Réunion',
  'liban': 'Liban',
  'caledonie': 'Nouvelle Calédonie'
};

const SubjectsFilters = () => {
  const {
    selectedType,
    setSelectedType,
    selectedChapter,
    setSelectedChapter,
    selectedYear,
    setSelectedYear,
    selectedSession,
    setSelectedSession,
    selectedLocation,
    setSelectedLocation,
    searchQuery,
    setSearchQuery
  } = useSubjects();

  const resetFilters = () => {
    setSelectedType('');
    setSelectedChapter('');
    setSelectedYear('');
    setSelectedSession('');
    setSelectedLocation('');
    setSearchQuery('');
  };

  const hasActiveFilters = selectedType || selectedChapter || selectedYear || 
                          selectedSession || selectedLocation || searchQuery;

  return (
    <div className="bg-white rounded-xl shadow-lg border-2 border-[#ceace8] p-6 mb-8">
      <div className="space-y-4">
        {/* Barre de recherche */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Rechercher un sujet..."
            className="w-full pl-10 pr-4 py-2.5 rounded-lg border-2 border-[#9a6ebd] text-[#323c5f] placeholder-gray-400 focus:ring-[#9a6ebd] focus:ring-opacity-50 focus:border-[#9a6ebd]"
          />
        </div>

        {/* Filtres */}
        <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
          <select
            value={selectedType}
            onChange={(e) => setSelectedType(e.target.value)}
            className="rounded-lg border-2 border-[#9a6ebd] text-[#323c5f] py-2.5 px-4 focus:ring-[#9a6ebd] focus:ring-opacity-50 focus:border-[#9a6ebd] bg-white hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <option value="">Type d'épreuve</option>
            <option value="dissertation">Dissertation</option>
            <option value="ec1">EC1 - Mobilisation</option>
            <option value="ec2">EC2 - Analyse</option>
            <option value="ec3">EC3 - Raisonnement</option>
          </select>

          <select
            value={selectedChapter}
            onChange={(e) => setSelectedChapter(e.target.value)}
            className="rounded-lg border-2 border-[#9a6ebd] text-[#323c5f] py-2.5 px-4 focus:ring-[#9a6ebd] focus:ring-opacity-50 focus:border-[#9a6ebd] bg-white hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <option value="">Chapitre</option>
            {Object.entries(chapterLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>

          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
            className="rounded-lg border-2 border-[#9a6ebd] text-[#323c5f] py-2.5 px-4 focus:ring-[#9a6ebd] focus:ring-opacity-50 focus:border-[#9a6ebd] bg-white hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <option value="">Année</option>
            <option value="2024">2024</option>
            <option value="2023">2023</option>
            <option value="2022">2022</option>
            <option value="2021">2021</option>
          </select>

          <select
            value={selectedSession}
            onChange={(e) => setSelectedSession(e.target.value)}
            className="rounded-lg border-2 border-[#9a6ebd] text-[#323c5f] py-2.5 px-4 focus:ring-[#9a6ebd] focus:ring-opacity-50 focus:border-[#9a6ebd] bg-white hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <option value="">Session</option>
            <option value="normale">Session normale</option>
            <option value="remplacement">Session de remplacement</option>
            <option value="candidat-libre">Candidat libre</option>
          </select>

          <select
            value={selectedLocation}
            onChange={(e) => setSelectedLocation(e.target.value)}
            className="rounded-lg border-2 border-[#9a6ebd] text-[#323c5f] py-2.5 px-4 focus:ring-[#9a6ebd] focus:ring-opacity-50 focus:border-[#9a6ebd] bg-white hover:bg-gray-50 transition-colors cursor-pointer"
          >
            <option value="">Centre d'examen</option>
            {Object.entries(locationLabels).map(([value, label]) => (
              <option key={value} value={value}>{label}</option>
            ))}
          </select>
        </div>

        {/* Bouton réinitialiser */}
        {hasActiveFilters && (
          <div className="flex justify-end">
            <button
              onClick={resetFilters}
              className="inline-flex items-center px-4 py-2 text-[#9a6ebd] hover:bg-[#9a6ebd]/10 rounded-lg transition-colors"
            >
              <X size={16} className="mr-2" />
              Réinitialiser les filtres
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default SubjectsFilters;